import React, { memo, useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { t } from 'i18next';

import styles from './styles.module.css';

type QrCodeScanProps = {
    onScan: (data: string) => void;
};

const QrCodeScan = memo(({ onScan }: QrCodeScanProps) => {
    const [hasScanError, setHasScanError] = useState(false);
    const [isScanning, setIsScanning] = useState(true);
    const currentStream = useRef<MediaStream | undefined>(undefined);
    const currentVideo = useRef<HTMLVideoElement | null>(null);
    const isComponentMounted = useRef(true);

    useEffect(() => {
        try {
            const hiddenCanvasElement = document.createElement('canvas');
            const video = currentVideo.current;
            const hiddenCanvas = hiddenCanvasElement.getContext('2d');

            navigator.mediaDevices
                .getUserMedia({ video: { facingMode: 'environment' } })
                .then(function (stream) {
                    try {
                        currentStream.current = stream;
                        if (video) {
                            video.srcObject = stream;
                            void video.play();
                        }
                        requestAnimationFrame(tick);
                        setHasScanError(false);
                    } catch (error) {
                        console.log(error);
                        setHasScanError(true);
                    }
                })
                .catch((error) => {
                    console.log(error);
                    setHasScanError(true);
                });

            const tick = () => {
                if (isComponentMounted.current && video?.readyState === video?.HAVE_ENOUGH_DATA) {
                    if (video && video.style != null) {
                        video.style.objectFit = 'cover';
                    }
                    hiddenCanvasElement.hidden = true;

                    const scanAreaSize = 250;
                    const scanAreaX = ((video?.videoWidth ?? scanAreaSize) - scanAreaSize) / 2.25;
                    const scanAreaY = ((video?.videoHeight ?? scanAreaSize) - scanAreaSize) / 2.75;

                    hiddenCanvasElement.width = scanAreaSize;
                    hiddenCanvasElement.height = scanAreaSize;

                    if (video) {
                        hiddenCanvas?.drawImage(
                            video,
                            scanAreaX,
                            scanAreaY,
                            scanAreaSize,
                            scanAreaSize,
                            0,
                            0,
                            scanAreaSize,
                            scanAreaSize,
                        );
                    }

                    const imageData = hiddenCanvas?.getImageData(0, 0, scanAreaSize, scanAreaSize);
                    let code = null;
                    if (imageData) {
                        code = jsQR(imageData.data, imageData.width, imageData.height, {
                            inversionAttempts: 'dontInvert',
                        });
                    }
                    if (code) {
                        onScan(code.data);
                        setIsScanning(false);
                    } else {
                        setIsScanning(true);
                    }
                }
                if (isComponentMounted.current) {
                    requestAnimationFrame(tick);
                }
            };
        } catch (error) {
            console.log(error);
            setHasScanError(true);
        }
        return () => {
            isComponentMounted.current = false;
            if (currentStream.current) {
                const tracks = currentStream.current.getTracks();

                tracks.forEach((track) => {
                    track.stop();
                });
            }
        };
    }, [onScan]);

    return (
        <div className={styles.wrapper}>
            {!hasScanError ? (
                <div className={styles.container}>
                    <video ref={currentVideo} className={styles.video} playsInline={true}></video>
                    <div className={`${styles.scanArea} ${!isScanning ? styles.scanned : styles.scanning}`}></div>
                </div>
            ) : (
                <h3 className={styles.noSupport}>{t('qr-code.browser-not-support')}</h3>
            )}
        </div>
    );
});

export default QrCodeScan;
